import React from 'react';
import classNames from 'classnames';

import { FormattedMessage, useIntl } from '../../util/reactIntl';
import { formatMoney } from '../../util/currency';
import {
  getHireRateRows,
  getReviewsSummary,
} from '../../util/listingPageContent';
import { getApplicableHireRateKey } from '../../util/hireRates';
import { IconDollar, IconTierSelected, IconTierUnselected, IconStarFill } from './ListingIcons';

import css from './HireRateTiers.module.css';

const TIER_LABEL_IDS = {
  hireRateShortTerm: 'ListingPage.tierShortTerm',
  hireRateMediumTerm: 'ListingPage.tierMediumTerm',
  hireRateLongTerm: 'ListingPage.tierLongTerm',
  hireRateMarathonTerm: 'ListingPage.tierMarathon',
};

/**
 * Hire rate tiers for the listing OrderPanel.
 * Display-only: booking day count auto-selects the matching tier (not clickable).
 *
 * @component
 */
const HireRateTiers = props => {
  const intl = useIntl();
  const {
    publicData,
    price,
    marketplaceCurrency,
    reviews,
    bookingDayCount = null,
    className,
  } = props;
  const rows = getHireRateRows(
    publicData,
    marketplaceCurrency,
    money => formatMoney(intl, money),
    price
  );
  const availableKeys = rows.map(r => r.key);
  const applicableKey = getApplicableHireRateKey(bookingDayCount, availableKeys);
  // Fallback to short-term until dates are chosen; then follow booking length.
  const selectedKey = applicableKey || rows[0]?.key || null;

  if (!rows.length) {
    return null;
  }

  const selected = rows.find(r => r.key === selectedKey) || rows[0];
  const reviewsSummary = getReviewsSummary(reviews);

  return (
    <div className={classNames(css.root, className)}>
      <div className={css.headerWrapper}>

      <div className={css.header}>
        <span className={css.headerIcon}>
          <IconDollar />
        </span>
        <h2 className={css.title}>
          <FormattedMessage id="ListingPage.hireRateTitle" />
        </h2>
          </div>
          <p className={css.selectedPrice}>
          <span className={css.selectedPriceValue}>{selected.formattedPrice}</span>
          <span className={css.selectedPerDay}>
            /<FormattedMessage id="ListingPage.perDayShort" />
          </span>
        </p>
      </div>
    

      <div
        className={css.tierList}
        role="list"
        aria-label={intl.formatMessage({ id: 'ListingPage.hireRateTitle' })}
      >
        {rows.map(row => {
          const isSelected = row.key === selected.key;
          return (
            <div
              key={row.key}
              role="listitem"
              className={classNames(css.tierRow, { [css.tierRowSelected]: isSelected })}
              aria-current={isSelected ? 'true' : undefined}
            >
              <span className={css.tierIcon}>
                {isSelected ? <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M13.3346 1.6665V4.99984M6.66797 1.6665V4.99984" stroke="#3F6EF5" stroke-linecap="round" stroke-linejoin="round" fill="none" />
                  <path d="M10.8333 3.33331H9.16667C6.02397 3.33331 4.45262 3.33331 3.47631 4.30962C2.5 5.28594 2.5 6.85728 2.5 9.99998V11.6666C2.5 14.8093 2.5 16.3807 3.47631 17.357C4.45262 18.3333 6.02397 18.3333 9.16667 18.3333H10.8333C13.976 18.3333 15.5474 18.3333 16.5237 17.357C17.5 16.3807 17.5 14.8093 17.5 11.6666V9.99998C17.5 6.85728 17.5 5.28594 16.5237 4.30962C15.5474 3.33331 13.976 3.33331 10.8333 3.33331Z" stroke="#3F6EF5" stroke-linecap="round" stroke-linejoin="round" fill="none" />
                  <path d="M2.5 8.33331H17.5" stroke="#3F6EF5" stroke-width="0.833333" stroke-linecap="round" stroke-linejoin="round" fill="none" />
                </svg>
                  : <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M13.3346 1.6665V4.99984M6.66797 1.6665V4.99984" stroke="#64748B" stroke-linecap="round" stroke-linejoin="round" fill="none" />
                    <path d="M10.8333 3.33331H9.16667C6.02397 3.33331 4.45262 3.33331 3.47631 4.30962C2.5 5.28594 2.5 6.85728 2.5 9.99998V11.6666C2.5 14.8093 2.5 16.3807 3.47631 17.357C4.45262 18.3333 6.02397 18.3333 9.16667 18.3333H10.8333C13.976 18.3333 15.5474 18.3333 16.5237 17.357C17.5 16.3807 17.5 14.8093 17.5 11.6666V9.99998C17.5 6.85728 17.5 5.28594 16.5237 4.30962C15.5474 3.33331 13.976 3.33331 10.8333 3.33331Z" stroke="#64748B" stroke-linecap="round" stroke-linejoin="round" fill="none" />
                    <path d="M2.5 8.33331H17.5" stroke="#64748B" stroke-width="0.833333" stroke-linecap="round" stroke-linejoin="round" fill="none" />
                  </svg>
                }
              </span>
              
              <span className={css.tierText}>
                <span className={css.tierRange}>
                  <FormattedMessage id={row.tierRangeId} />
                </span>
                <span className={css.tierLabel}>
                  <FormattedMessage id={TIER_LABEL_IDS[row.key] || row.tierLabelId} />
                </span>
              </span>
              <span className={css.tierRight}>
                {isSelected ? (
                  <span className={css.selectedBadge}>
                    <FormattedMessage id="ListingPage.hireRateSelected" />
                  </span>
                ) : null}
                <span className={css.tierPrice}>
                  <span className={css.tierPriceValue}>{row.formattedPrice}</span>
                  <span className={css.tierPerDay}>
                    /<FormattedMessage id="ListingPage.perDayShort" />
                  </span>
                </span>
              </span>
            </div>
          );
        })}
      </div>

      <div className={css.selectedSummary}>
      
        {reviewsSummary ? (
          <div className={css.reviewsLine}>
            <span className={css.starIcon}>
              <IconStarFill />
            </span>
            <span className={css.reviewsAverage}>{reviewsSummary.average.toFixed(2)}</span>
            <span className={css.reviewsDot} aria-hidden="true" />
            <span className={css.reviewsCount}>
              <FormattedMessage
                id="ListingPage.hireRateReviews"
                values={{ count: reviewsSummary.count }}
              />
            </span>
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default HireRateTiers;
